/**
 * Feature flags — PILOT_MODE (Grozd pilot, grana pilot-09-2026).
 *
 * Grozd pilot (F1) servira samo dio ERP-a: partneri, artikli, fakture.
 * Van-pilot moduli (magacin, banka, knjigovodstvo, zaposleni) se SAKRIVAJU
 * iz navigacije da klijent ne dobije dugmad koja vode na 501/mock ekrane.
 *
 * Uključeno kroz env varijablu NEXT_PUBLIC_PILOT_MODE:
 *   - nedefinisano / "1" / "true" / "on" → pilot ON (default na ovoj grani)
 *   - "0" / "false" / "off" / "no"        → pilot OFF (pun ERP, sve vidljivo)
 *
 * NAPOMENA: NEXT_PUBLIC_ prefiks je obavezan — sve nav komponente su "use client",
 * pa se flag mora ubaciti u client bundle u build-time.
 */

export type PilotModule =
  | "partneri"
  | "artikli"
  | "fakture"
  | "magacin"
  | "banka"
  | "knjigovodstvo"
  | "zaposleni";

/** Moduli vidljivi u pilotu. Sve van ovog seta se sakriva kad je PILOT_MODE ON. */
export const PILOT_MODULES: readonly PilotModule[] = ["partneri", "artikli", "fakture"];

function readPilotMode(): boolean {
  const raw = (process.env.NEXT_PUBLIC_PILOT_MODE ?? "").trim().toLowerCase();
  if (raw === "0" || raw === "false" || raw === "off" || raw === "no") return false;
  // Default: pilot je UKLJUČEN na grani pilot-09-2026.
  return true;
}

/** Da li je aplikacija u pilot režimu (van-pilot moduli sakriveni). */
export const PILOT_MODE: boolean = readPilotMode();

/**
 * Mapiranje href prefiksa → van-pilot modul. Sve što nije ovdje smatra se
 * dozvoljenim (core: dashboard/pregled, podešavanja, aktivnost + pilot moduli).
 */
const OUT_OF_PILOT_PREFIXES: ReadonlyArray<readonly [string, PilotModule]> = [
  ["/magacin", "magacin"],
  ["/banka", "banka"],
  ["/racunovodstvo", "knjigovodstvo"],
  ["/knjigovodstvo", "knjigovodstvo"],
  ["/plate", "zaposleni"],
  ["/zaposleni", "zaposleni"],
];

/** Da li je modul (po ključu) omogućen u trenutnom režimu. */
export function isModuleEnabled(module: PilotModule): boolean {
  if (!PILOT_MODE) return true;
  return PILOT_MODULES.includes(module);
}

/**
 * Da li je van-pilot modul po njegovom URL prefiksu (za route guard).
 * Vraća modul ključ ako href pripada nekom van-pilot modulu, inače null.
 */
export function outOfPilotModuleForHref(href: string): PilotModule | null {
  const path = (href.split("?")[0] || "").split("#")[0];
  for (const [prefix, module] of OUT_OF_PILOT_PREFIXES) {
    if (path === prefix || path.startsWith(prefix + "/")) return module;
  }
  return null;
}

/**
 * Da li je nav link (href) dozvoljen u trenutnom režimu.
 * Van-pilot prefiksi se sakrivaju kad je PILOT_MODE ON; sve ostalo prolazi.
 */
export function isHrefEnabled(href: string): boolean {
  if (!PILOT_MODE) return true;
  const module = outOfPilotModuleForHref(href);
  return module === null ? true : isModuleEnabled(module);
}
